import React, { useState } from 'react';
import { Plus, X, Copy, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

interface VariationManagerProps {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  maxLength?: number;
  helpText?: string;
  minVariations?: number;
  maxVariations?: number;
}

export default function VariationManager({
  label,
  value = [],
  onChange,
  placeholder = 'Enter variation',
  maxLength,
  helpText,
  minVariations = 1,
  maxVariations = 15
}: VariationManagerProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const variations = value.length > 0 ? value : [''];

  const handleAdd = () => {
    if (variations.length < maxVariations) {
      onChange([...variations, '']);
      setEditingIndex(variations.length);
    }
  };

  const handleRemove = (index: number) => {
    if (variations.length > minVariations) {
      const newVariations = variations.filter((_, i) => i !== index);
      onChange(newVariations.filter(v => v.trim() !== ''));
      if (editingIndex === index) setEditingIndex(null);
    }
  };

  const handleDuplicate = (index: number) => {
    if (variations.length < maxVariations && variations[index]) {
      const newVariations = [
        ...variations.slice(0, index + 1),
        variations[index],
        ...variations.slice(index + 1)
      ];
      onChange(newVariations);
    }
  };

  const handleChange = (index: number, text: string) => {
    const newVariations = [...variations];
    newVariations[index] = text;
    onChange(newVariations.filter(v => v.trim() !== ''));
  };

  const handleMoveUp = (index: number) => {
    if (index > 0) {
      const newVariations = [...variations];
      [newVariations[index - 1], newVariations[index]] = [newVariations[index], newVariations[index - 1]];
      onChange(newVariations);
    }
  };

  const handleMoveDown = (index: number) => {
    if (index < variations.length - 1) {
      const newVariations = [...variations];
      [newVariations[index], newVariations[index + 1]] = [newVariations[index + 1], newVariations[index]];
      onChange(newVariations);
    }
  };

  const getCharCount = (text: string) => {
    return maxLength ? `${text.length}/${maxLength}` : `${text.length} chars`;
  };

  const isOverLimit = (text: string) => {
    return maxLength ? text.length > maxLength : false;
  };

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          <span className="ml-2 text-xs text-gray-500">
            ({variations.filter(v => v.trim()).length}/{maxVariations} variations)
          </span>
        </label>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-gray-400 hover:text-gray-600"
        >
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {helpText && (
        <p className="text-xs text-gray-500">{helpText}</p>
      )}

      {isExpanded && (
        <div className="space-y-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
          {/* Variation List */}
          {variations.map((variation, index) => (
            <div
              key={index}
              className="flex items-start gap-2 bg-white border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors"
            >
              {/* Variation Number */}
              <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-semibold mt-2">
                {index + 1}
              </div>

              {/* Input Area */}
              <div className="flex-1 space-y-1">
                <textarea
                  value={variation}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onFocus={() => setEditingIndex(index)}
                  onBlur={() => setEditingIndex(null)}
                  placeholder={`${placeholder} #${index + 1}`}
                  rows={2}
                  maxLength={maxLength}
                  className={`w-full px-3 py-2 border rounded-md text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isOverLimit(variation)
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-gray-300'
                  }`}
                />
                <div className="flex items-center justify-between text-xs">
                  <span className={isOverLimit(variation) ? 'text-red-600 font-medium' : 'text-gray-500'}>
                    {getCharCount(variation)}
                  </span>
                  {variation.trim() && (
                    <span className="text-green-600">✓ Valid</span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-1">
                {/* Move Up/Down */}
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === variations.length - 1}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>

                {/* Duplicate */}
                <button
                  type="button"
                  onClick={() => handleDuplicate(index)}
                  disabled={variations.length >= maxVariations || !variation.trim()}
                  className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Duplicate"
                >
                  <Copy className="w-4 h-4" />
                </button>

                {/* Remove */}
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  disabled={variations.length <= minVariations}
                  className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {/* Add Button */}
          {variations.length < maxVariations && (
            <button
              type="button"
              onClick={handleAdd}
              className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Variation ({variations.length}/{maxVariations})
            </button>
          )}

          {/* Tips */}
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
            <p className="font-semibold mb-1">💡 Tips for Better Variations:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Test different value propositions and messaging angles</li>
              <li>Vary the call-to-action strength and urgency</li>
              <li>Include both benefit-focused and feature-focused copy</li>
              <li>Keep important keywords in all variations for consistency</li>
              {maxLength && <li>Stay within the {maxLength} character limit for best performance</li>}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}